import { useDndMonitor, useDraggable } from '@dnd-kit/core';
import { type SerializedTodoItem } from '@pique/core/modules/properties/services/schema';
import type React from 'react';
import { useState } from 'react';

import { STATUS_COLORS } from './config/status-config';
import * as styles from './ActionItemFocusList.css';
import { TodoItemComponent } from './TodoItemComponent';

const MAX_IN_PROGRESS = 3;

interface ActionItemFocusListProps {
  items: SerializedTodoItem[];
  onItemComplete?: (itemId: string, pageId: string) => void;
  dropIndicatorVisible?: boolean;
  dropIndicatorItem?: SerializedTodoItem | null;
}

const DraggableItem: React.FC<{
  item: SerializedTodoItem;
  onItemComplete?: (itemId: string, pageId: string) => void;
  dragHidden?: boolean;
}> = ({ item, onItemComplete, dragHidden }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useDraggable({ id: item.id });

  const hidden = isDragging || dragHidden;
  const style: React.CSSProperties = {
    opacity: hidden ? 0 : 1,
    visibility: hidden ? 'hidden' : 'visible',
    pointerEvents: hidden ? 'none' : 'auto',
    position: 'relative',
    cursor: 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <TodoItemComponent
        item={item}
        onComplete={onItemComplete}
        suppressFocusZone
        compact
        isDraggable
      />
    </div>
  );
};

export const ActionItemFocusList: React.FC<ActionItemFocusListProps> = ({
  items,
  onItemComplete,
  dropIndicatorVisible = false,
  dropIndicatorItem = null,
}) => {
  const [activeDragItemId, setActiveDragItemId] = useState<string | null>(null);
  const inProgressItems = items.slice(0, MAX_IN_PROGRESS);

  useDndMonitor({
    onDragStart: event => {
      setActiveDragItemId(String(event.active.id));
    },
    onDragEnd: () => {
      setActiveDragItemId(null);
    },
    onDragCancel: () => {
      setActiveDragItemId(null);
    },
  });

  const isEmpty = inProgressItems.length === 0;

  return (
    <div
      className={styles.focusListContainer}
      data-drop-active={dropIndicatorVisible || undefined}
      data-has-items={inProgressItems.length > 0 || undefined}
    >
      <div className={styles.sectionHeader}>
        <span
          className={styles.sectionLabel}
          style={{ color: STATUS_COLORS.IN_PROGRESS }}
        >
          In Progress
        </span>
        <span
          className={styles.sectionCount}
          style={{ color: STATUS_COLORS.IN_PROGRESS, opacity: 0.6 }}
        >
          {inProgressItems.length}/{MAX_IN_PROGRESS}
        </span>
      </div>
      {dropIndicatorVisible && dropIndicatorItem && (
        <div style={{ opacity: 0.4, pointerEvents: 'none' }} aria-hidden>
          <TodoItemComponent item={dropIndicatorItem} compact onComplete={() => {}} />
        </div>
      )}
      {isEmpty && !dropIndicatorVisible && (
        <div className={styles.emptyZone}>Drop to start</div>
      )}
      {inProgressItems.length > 0 && (
        <div className={styles.sectionItems}>
          {inProgressItems.map(item => (
            <DraggableItem
              key={item.id}
              item={item}
              onItemComplete={onItemComplete}
              dragHidden={activeDragItemId === item.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};
